export function giftPlan(cart, config, now = Date.now(), declined = false) {
  if (!cart || !Array.isArray(cart.items)) throw new Error('Invalid cart response');
  const gifts = cart.items.filter(item => String(item.variant_id) === String(config.giftVariant));
  const devices = cart.items.filter(item => String(item.variant_id) === String(config.deviceVariant) && item.quantity > 0);
  const eligible = config.enabled && now >= Date.parse(config.startsAt) && now < Date.parse(config.endsAt) && devices.length > 0 && !declined;
  if (!eligible) return gifts.length ? { type: 'remove', keys: gifts.map(item => item.key) } : { type: 'done' };
  if (gifts.some(item => item.final_price !== 0)) return { type: 'priced_gift', keys: gifts.map(item => item.key) };
  const valid = gifts.find(item => devices.some(device => item.parent_relationship?.parent_key === device.key));
  const extra = gifts.filter(item => item !== valid);
  if (extra.length) return { type: 'remove', keys: extra.map(item => item.key) };
  if (valid) return valid.quantity === 1 ? { type: 'done' } : { type: 'quantity', key: valid.key, quantity: 1 };
  return { type: 'add', item: { id: Number(config.giftVariant), quantity: 1, parent_line_key: devices[0].key, properties: { _evebra_october_gift: '2026-10' } } };
}

export function createGiftManager(config, api, options = {}) {
  let running = null;
  let rerun = false;
  const clock = options.now || Date.now;
  const declined = options.declined || (() => false);
  async function settle() {
    let changed = false;
    let cart;
    for (let pass = 0; pass < 10; pass++) {
      cart = await api.read();
      const plan = giftPlan(cart, config, clock(), declined());
      if (plan.type === 'done') {
        if (changed) await api.render(cart);
        return cart;
      }
      if (plan.type === 'remove' || plan.type === 'priced_gift') {
        await api.change(plan.keys[0], 0);
        changed = true;
        if (plan.type === 'priced_gift') {
          await api.render(await api.read());
          throw new Error('Gift price must be zero');
        }
      } else if (plan.type === 'quantity') {
        await api.change(plan.key, plan.quantity);
        changed = true;
      } else {
        await api.add(plan.item);
        changed = true;
      }
    }
    throw new Error('Gift reconciliation did not settle');
  }
  return {
    sync() {
      if (running) { rerun = true; return running; }
      running = (async () => {
        let cart;
        do { rerun = false; cart = await settle(); } while (rerun);
        return cart;
      })().finally(() => { running = null; });
      return running;
    }
  };
}

export function giftCartHref(href, origin) {
  const url = new URL(href, origin);
  if (url.origin !== new URL(origin).origin) return href;
  if (/\/cart\/\d+:\d+/.test(url.pathname)) url.searchParams.set('storefront', 'true');
  if (/\/discount\//.test(url.pathname) && url.searchParams.has('redirect')) {
    const redirect = new URL(url.searchParams.get('redirect'), origin);
    if (redirect.origin === url.origin && /\/cart\/\d+:\d+/.test(redirect.pathname)) {
      redirect.searchParams.set('storefront', 'true');
      url.searchParams.set('redirect', redirect.pathname + redirect.search);
    }
  }
  return url.href;
}

export function mountGift(config, scope = window) {
  if (!config.enabled) return;
  const doc = scope.document;
  const root = config.root.endsWith('/') ? config.root : config.root + '/';
  const originalFetch = scope.fetch.bind(scope);
  function configureDeviceForms() {
    const active = Date.now() >= Date.parse(config.startsAt) && Date.now() < Date.parse(config.endsAt);
    for (const form of doc.querySelectorAll('form[action*="/cart/add"]')) {
      const isDevice = String(form.querySelector('[name="id"]')?.value) === String(config.deviceVariant);
      const accelerated = form.querySelector('.shopify-payment-button');
      if (accelerated && active && isDevice && !accelerated.hidden) {
        accelerated.dataset.ebGiftHidden = 'true';
        accelerated.hidden = true;
      } else if (accelerated?.dataset.ebGiftHidden && (!active || !isDevice)) {
        accelerated.hidden = false;
        delete accelerated.dataset.ebGiftHidden;
      }
    }
  }
  configureDeviceForms();
  let formRefreshPending = false;
  const observer = new scope.MutationObserver(() => {
    if (formRefreshPending) return;
    formRefreshPending = true;
    scope.requestAnimationFrame(() => { formRefreshPending = false; configureDeviceForms(); });
  });
  observer.observe(doc.body, { childList: true, subtree: true });
  doc.addEventListener('change', configureDeviceForms);
  const storageKey = 'evebra-october-2026-gift-declined';
  let latestCart = null;
  let declined = false;
  try { declined = scope.sessionStorage.getItem(storageKey) === 'yes'; } catch {}
  function decline() {
    declined = true;
    try { scope.sessionStorage.setItem(storageKey, 'yes'); } catch {}
  }
  async function json(path, body) {
    const response = await originalFetch(root + path, {
      method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: { 'Accept': 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    const data = await response.json();
    if (!response.ok || data.errors || data.status >= 400) throw new Error('Cart update failed');
    return data;
  }
  function status(message) {
    const el = doc.getElementById('eb-october-bag-status');
    if (el) { el.textContent = message; el.hidden = !message; }
  }
  async function render(cart) {
    const ids = ['cart-drawer', 'cart-icon-bubble'];
    for (const id of ['main-cart-items', 'main-cart-footer']) {
      const section = doc.getElementById(id)?.dataset.id;
      if (section) ids.push(section);
    }
    const url = new URL(scope.location.href);
    url.searchParams.set('sections', ids.join(','));
    const response = await originalFetch(url, { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error('Cart display refresh failed');
    const sections = await response.json();
    const parser = new scope.DOMParser();
    const pairs = [['cart-drawer', '#CartDrawer', '#CartDrawer'], ['cart-icon-bubble', '#cart-icon-bubble', '.shopify-section']];
    for (const id of ['main-cart-items', 'main-cart-footer']) {
      const section = doc.getElementById(id)?.dataset.id;
      if (section) pairs.push([section, '#' + id + ' .js-contents', '#' + id + ' .js-contents']);
    }
    for (const [id, selector, sourceSelector] of pairs) {
      const target = doc.querySelector(selector);
      if (!target) continue;
      if (typeof sections[id] !== 'string') throw new Error('Missing cart section');
      const source = parser.parseFromString(sections[id], 'text/html').querySelector(sourceSelector);
      if (!source) throw new Error('Missing cart markup');
      target.replaceChildren(...Array.from(source.childNodes));
    }
    doc.querySelector('cart-drawer')?.classList.toggle('is-empty', cart.item_count === 0);
    doc.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => doc.querySelector('cart-drawer')?.close());
    doc.dispatchEvent(new scope.CustomEvent('evebra:gift-updated', { detail: { cart } }));
  }
  const manager = createGiftManager(config, {
    async read() { latestCart = await json('cart.js'); return latestCart; },
    add: item => json('cart/add.js', { items: [item] }),
    change: (key, quantity) => json('cart/change.js', { id: key, quantity }),
    render
  }, { declined: () => declined });
  let timer;
  const pendingMutations = new Set();
  function schedule() {
    scope.clearTimeout(timer);
    timer = scope.setTimeout(() => manager.sync().then(() => status('')).catch(() => status(config.error)), 350);
  }
  function identifyRemoval(input, init) {
    try {
      const url = new URL(typeof input === 'string' ? input : input.url, scope.location.href);
      if (!url.pathname.endsWith('/cart/change.js') || typeof init?.body !== 'string') return;
      const body = JSON.parse(init.body);
      if (Number(body.quantity) !== 0) return;
      const item = body.line ? latestCart?.items[Number(body.line) - 1] : latestCart?.items.find(i => i.key === body.id || String(i.variant_id) === String(body.id));
      if (item && String(item.variant_id) === String(config.giftVariant)) decline();
    } catch {}
  }
  scope.fetch = async function(input, init) {
    identifyRemoval(input, init);
    let mutation = false;
    try {
      const url = new URL(typeof input === 'string' ? input : input.url, scope.location.href);
      mutation = url.origin === scope.location.origin && /\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname);
    } catch {}
    const request = originalFetch(input, init);
    if (mutation) pendingMutations.add(request);
    try {
      const response = await request;
      if (mutation && response.ok) schedule();
      return response;
    } finally { pendingMutations.delete(request); }
  };
  let exiting = false;
  async function checkout(event, destination) {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (exiting) return;
    exiting = true;
    status(config.checking);
    try {
      while (pendingMutations.size) await Promise.allSettled(Array.from(pendingMutations));
      await manager.sync();
      scope.location.assign(destination);
    } catch {
      exiting = false;
      status(config.error);
    }
  }
  doc.addEventListener('click', event => {
    const target = event.target.closest?.('a[href],button[name="checkout"],input[name="checkout"]');
    if (!target || event.defaultPrevented) return;
    if (target.matches('[name="checkout"]')) { checkout(event, root + 'checkout'); return; }
    const url = new URL(target.href, scope.location.href);
    const routed = giftCartHref(target.href, scope.location.origin);
    if (routed !== url.href) target.href = routed;
    if (url.origin === scope.location.origin && /\/checkout\/?$/.test(url.pathname)) checkout(event, url.href);
  }, true);
  doc.addEventListener('submit', event => {
    if (event.submitter?.name === 'checkout') checkout(event, root + 'checkout');
  }, true);
  doc.addEventListener('click', event => {
    const button = event.target.closest?.('cart-remove-button');
    const item = button && latestCart?.items[Number(button.dataset.index) - 1];
    if (item && String(item.variant_id) === String(config.giftVariant)) decline();
  }, true);
  scope.addEventListener('pageshow', schedule);
  doc.addEventListener('visibilitychange', () => { if (!doc.hidden) schedule(); });
  doc.addEventListener('cart:change', schedule);
  schedule();
  return manager;
}

if (typeof document !== 'undefined') {
  const node = document.getElementById('eb-october-bag-config');
  if (node) mountGift(JSON.parse(node.textContent));
}
